import React, { FC, useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Question } from '../../models/QAndA';
import DataRoomContext from '../../providers/DataRoom/DataRoom.context';
import PluscircleIcon from 'icons/Pluscircle.icon';
import DisplayRichTextString from 'components/RichTextEditorField/components/DisplayRichTextString';
import { getUserFullName } from 'utils';
import QuestionFiles from '../QuestionFiles';
import AnswerFiles from '../AnswerFiles';

import './QuestionPreview.styles.scss';

type QuestionPreviewProps = {
  question?: Question;
  hidePrivateInfo?: boolean;
  displayedQuestions: Question[];
  updateQuestion: (question: Partial<Question>) => void;
};

const QuestionPreview: FC<QuestionPreviewProps> = ({
  question,
  displayedQuestions,
  hidePrivateInfo,
  updateQuestion,
}) => {
  const { t } = useTranslation();

  const {
    dataRoomParentQuestion,
    dataRoomLatestFollowupQuestion,
    dataRoomPreviousFollowupQuestion,
  } = question;

  const { isBuySide } = useContext(DataRoomContext);

  const showSellSideInfo = !isBuySide;

  const hasFollowUpQuestion =
    !!dataRoomLatestFollowupQuestion ||
    !!dataRoomPreviousFollowupQuestion ||
    !!dataRoomParentQuestion;

  const displayQuestion = useCallback(
    (question: Question, isOriginalQuestion?: boolean) => (
      <div className="question-previews__preview">
        <div className="question-previews__preview__question">
          {hasFollowUpQuestion && (
            <span className="question-previews__preview__follow-up">
              {isOriginalQuestion ? (
                t('originalQuestion')
              ) : (
                <>
                  <PluscircleIcon />
                  {t('followUpQuestion')}
                </>
              )}
            </span>
          )}

          <DisplayRichTextString
            modalTitle={question.subject}
            className="question-previews__preview__question__text"
          >
            {question.content}
          </DisplayRichTextString>

          <QuestionFiles
            questionId={question._id}
            files={question.files}
            updateQuestion={(files) => updateQuestion({ ...question, files })}
          />

          <span className="question-previews__preview__question__subject">
            <b>{question.subject}</b>
            {!hidePrivateInfo && (
              <>
                <span className="dot" />
                {t('by')}{' '}
                {getUserFullName(
                  question.user?.firstName,
                  question.user?.lastName,
                )}
              </>
            )}
          </span>
        </div>

        {question.dataRoomAnswer?.content && (
          <div className="question-previews__preview__answer">
            {!hidePrivateInfo && showSellSideInfo && (
              <span className="question-previews__preview__answer__author">
                <p>{t('manageAnswerSellOptions.answer')} </p>
                {t('by')}{' '}
                {getUserFullName(
                  question.dataRoomAnswer.user?.firstName,
                  question.dataRoomAnswer.user?.lastName,
                ) || t('unknown')}
              </span>
            )}

            <DisplayRichTextString
              modalTitle={t('manageAnswerSellOptions.answer')}
            >
              {question.dataRoomAnswer?.content}
            </DisplayRichTextString>

            <AnswerFiles
              answerId={question.dataRoomAnswer._id}
              files={question.dataRoomAnswer?.files}
              updateAnswer={(files) =>
                updateQuestion({
                  ...question,
                  dataRoomAnswer: { ...question.dataRoomAnswer, files },
                })
              }
            />
          </div>
        )}
      </div>
    ),
    [hasFollowUpQuestion, hidePrivateInfo, showSellSideInfo, t, updateQuestion],
  );

  return (
    <div className="question-previews">
      {displayedQuestions?.map((question, index) =>
        displayQuestion(question, index === 0),
      )}
    </div>
  );
};

export default QuestionPreview;
